import React, { useEffect } from 'react';
import { Modal, Form, Input, Row, Col } from 'antd';
import {
  ButtonCommon,
  ErrorMessage,
  showAlertNotification,
  InputCommon,
  LabelWithRequired,
  SelectCommon,
  MemberRankBadge,
} from 'components/common';
import { EditMemberRankModalProps } from 'types/common';
import { options } from 'constant/options';
import { Rule } from 'antd/es/form';
import { formatPrice } from 'utils';
import { useWatch } from 'antd/es/form/Form';
const { TextArea } = Input;

const EditMemberRankModal: React.FC<EditMemberRankModalProps> = ({
  isOpen,
  onCancel,
  onSubmit,
  form,
  memberRankData = null,
  isSubmitting = false,
}) => {
  const validateMaxPoints = (_: Rule, value: string): Promise<void> => {
    if (!value || value.toString().trim() === '') {
      return Promise.resolve();
    }

    const maxPointsNum = Number(value.toString().replace(/,/g, ''));
    if (isNaN(maxPointsNum)) {
      return Promise.reject('Giá trị không hợp lệ');
    }

    const minPointsValue = form.getFieldValue('minPoints') || '0';
    const minPointsNum = Number(minPointsValue.toString().replace(/,/g, ''));

    if (!isNaN(minPointsNum) && maxPointsNum <= minPointsNum) {
      return Promise.reject('Hạn mức tối đa phải lớn hơn hạn mức tối thiểu');
    }

    return Promise.resolve();
  };

  const validateDiscount = (_: Rule, value: string): Promise<void> => {
    if (!value) {
      return Promise.reject('Vui lòng chọn mức giảm giá');
    }
    return Promise.resolve();
  };
  useEffect(() => {
    if (isOpen) {
      if (memberRankData) {
        form.setFieldsValue({
          name: memberRankData.name,
          description: memberRankData.description,
          minPoints: memberRankData.minPoints || 0,
          maxPoints: memberRankData.maxPoints || 0,
          isActive: memberRankData.isActive,
          color: memberRankData.color,
          discount: memberRankData.discount?.toString(),
        });
      }
    }
  }, [isOpen, form, memberRankData]);
  const maxPoints = useWatch('maxPoints', form);
  return (
    <Modal
      title={
        <div className='flex flex-col bg-white py-[12px] px-[24px] rounded-lg gap-2'>
          <h2 className='text-[18px] font-bold leading-[24px]'>{'CHỈNH SỬA THÔNG TIN HẠNG THÀNH VIÊN'}</h2>
          <p className='text-[14px] leading-[20px] text-[var(--textBlack56)] font-normal'>
            Cài đặt hạng mức của hạng thành viên
          </p>
        </div>
      }
      open={isOpen}
      // onCancel={onCancel}
      footer={null}
      width={1255}
      centered
      className='staff-modal'
      destroyOnClose
      closeIcon={null}
      styles={{
        wrapper: {
          alignItems: 'center',
          paddingTop: '10vh',
        },
      }}
    >
      <div className='px-[24px] py-[12] mb-[12px]'>
        <div className='bg-white items-center flex gap-4 rounded-lg py-3 px-6 font-medium text-[14px] leading-[20px] mb-4'>
          <MemberRankBadge
            color={memberRankData?.color}
            name={memberRankData?.name || ''}
            className=' transition-all duration-200 hover:scale-105'
          />
          <div className='flex gap-2'>
            <span className='text-[var(--textBlack56)]'>Hạn mức tối thiểu</span>
            <span className='font-medium'>{`${formatPrice(useWatch('minPoints', form) || 0)} VNĐ`}</span>
          </div>

          <div className='flex gap-2'>
            <span className='text-[var(--textBlack56)]'>Hạn mức tối đa</span>
            <span className='font-medium'>{`${maxPoints === 0 || !maxPoints ? 'Không giới hạn' : formatPrice(maxPoints) + ' VNĐ'}`}</span>
          </div>
        </div>
        <Form
          form={form}
          requiredMark={false}
          layout='vertical'
          className='bg-white rounded-lg py-[24px] px-[48px] font-medium text-[14px] leading-[20px]'
          onValuesChange={(_, allValues) => {
            if (
              (allValues.maxPoints !== undefined && allValues.maxPoints !== null) ||
              (allValues.minPoints !== undefined && allValues.minPoints !== null)
            ) {
              form.validateFields(['minPoints', 'maxPoints']);
            }
          }}
        >
          <Row gutter={[24, 0]}>
            <Col span={8}>
              <Form.Item label={<LabelWithRequired label='Hạn mức tối thiểu' />} name={'minPoints'}>
                <InputCommon type='money' placeholder='Nhập vào hạn mức tối thiểu' />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label={'Hạn mức tối đa'}
                name={'maxPoints'}
                rules={[{ validator: validateMaxPoints }]}
                validateTrigger={['onChange', 'onBlur']}
              >
                <InputCommon
                  type='money'
                  placeholder={memberRankData?.maxPoints !== 0 ? 'Nhập vào hạn mức tối đa' : 'Không giới hạn'}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={<LabelWithRequired label='Giảm giá' />}
                name={'discount'}
                rules={[
                  { required: true, message: <ErrorMessage message='Vui lòng chọn mức giảm giá' /> },
                  { validator: validateDiscount },
                ]}
              >
                <SelectCommon placeholder={'Chọn mức giảm giá'} options={options} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                className='font-normal'
                label='Mô tả'
                name='description'
                rules={[{ max: 1000, message: <ErrorMessage message='Mô tả không được vượt quá 1000 ký tự!' /> }]}
              >
                <TextArea rows={4} placeholder='Nhập vào mô tả...' />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <div className='flex justify-center gap-3 mt-6'>
        <ButtonCommon color='none' size='md' radius='rounded' onClick={onCancel} disabled={isSubmitting}>
          Hủy
        </ButtonCommon>
        <ButtonCommon
          color='primary'
          size='md'
          radius='rounded'
          loading={isSubmitting}
          onClick={() => {
            form
              .validateFields()
              .then((values) => {
                const formattedValues = {
                  ...values,
                  maxPoints: values.maxPoints ? Number(values.maxPoints.toString().replace(/,/g, '')) : 0,
                  minPoints: values.minPoints ? Number(values.minPoints.toString().replace(/,/g, '')) : 0,
                  discount: Number(values.discount),
                };
                onSubmit(formattedValues);
              })
              .catch((errorInfo) => {
                showAlertNotification({
                  type: 'warning',
                  title: 'Thông báo',
                  message: 'Vui lòng nhập đúng thông tin hạng thành viên',
                });
                console.log('Validation failed:', errorInfo);
              });
          }}
        >
          Xác nhận
        </ButtonCommon>
      </div>
    </Modal>
  );
};

export default EditMemberRankModal;
